import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The open document review, kept in the URL rather than in component state.
 *
 * A reviewer works a queue of manifests over a sitting, and the position in that
 * queue was React state: a refresh, a back button or a link pasted to a
 * colleague all landed back at the start of the batch, or nowhere. Both the group
 * and the place in it now live in the query string, so all three do what they
 * look like they should.
 *
 * Unlike `?manifest=`, which is a one-shot that opens a dialog and immediately
 * drops itself, these params persist for as long as the review is open. That is
 * the whole point — a param that deletes itself cannot survive a refresh.
 */

/** The documents being worked, in order. */
export const REVIEW_BATCH_PARAM = "reviewBatch";
/** Which of them is open. */
export const REVIEW_DOC_PARAM = "reviewDoc";
/**
 * Whose tenant they belong to.
 *
 * Part of the address, not a convenience: the review resolves per-tenant feature
 * flags, and a cold load with no tenant resolves them to their defaults — so the
 * link would open a subtly different screen from the one that was shared.
 */
export const REVIEW_TENANT_PARAM = "reviewTenant";

/**
 * How many document ids a URL will carry.
 *
 * A batch is now a page of the review grid rather than one manifest's
 * documents, so this sits at the grid's default page size: the common case is
 * that everything on screen fits, and the reviewer works the queue they can
 * see. The cap itself is a guard against a URL long enough that a browser or a
 * chat client truncates it — a truncated batch would silently drop documents,
 * which is worse than a short one.
 */
const MAX_BATCH = 50;

/** A short id, as these are: UUIDs, so anything else is a malformed link. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseBatch(raw: string | null): string[] {
  if (raw == null || raw === "") return [];
  const ids = raw.split(",").filter((id) => UUID.test(id));
  // Deduplicated, because a repeated id would make `indexOf` ambiguous and the
  // reviewer would appear to be on two places in the queue at once.
  return [...new Set(ids)].slice(0, MAX_BATCH);
}

export interface ReviewQueueRoute {
  /** The batch, or empty when no review is open. */
  documentIds: string[];
  /** The open document, or undefined when no review is open. */
  currentDocumentId: string | undefined;
  /** The tenant the batch belongs to, or undefined when no review is open. */
  tenantId: string | undefined;
  /**
   * Open a batch, at `startAt` when given and at its first document otherwise.
   *
   * The tenant is optional because only a cross-tenant host needs it: the staff
   * queue resolves per-tenant feature flags and must say whose manifests these
   * are, while a customer uploading their own documents is already inside the
   * tenant the review will resolve against.
   */
  open: (documentIds: readonly string[], tenantId?: string, startAt?: string) => void;
  /** Move within the open batch. Pushes history, so Back steps back a document. */
  navigate: (documentId: string) => void;
  /** Close the review and clear both params. */
  close: () => void;
}

export function useReviewQueueRoute(): ReviewQueueRoute {
  const [searchParams, setSearchParams] = useSearchParams();

  const documentIds = useMemo(
    () => parseBatch(searchParams.get(REVIEW_BATCH_PARAM)),
    [searchParams]
  );

  const rawDoc = searchParams.get(REVIEW_DOC_PARAM);
  // Only honoured when it names a document actually in the batch. A hand-edited
  // or stale link otherwise opens the batch on a document the queue does not
  // contain, where "next" and "previous" have nothing to count from.
  const currentDocumentId =
    rawDoc != null && documentIds.includes(rawDoc) ? rawDoc : documentIds[0];

  const tenantId = searchParams.get(REVIEW_TENANT_PARAM) ?? undefined;

  const open = useCallback(
    (ids: readonly string[], forTenant?: string, startAt?: string) => {
      const batch = parseBatch(ids.join(","));
      if (batch.length === 0) return;
      // Only honoured when the batch contains it, for the same reason a
      // hand-edited link is: a document the queue does not hold is a place
      // "next" and "previous" cannot count from.
      const first = startAt != null && batch.includes(startAt) ? startAt : batch[0];
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(REVIEW_BATCH_PARAM, batch.join(","));
        next.set(REVIEW_DOC_PARAM, first);
        if (forTenant == null) next.delete(REVIEW_TENANT_PARAM);
        else next.set(REVIEW_TENANT_PARAM, forTenant);
        return next;
      });
    },
    [setSearchParams]
  );

  const navigate = useCallback(
    (documentId: string) => {
      // Pushed, not replaced: moving to the next document is a step the reviewer
      // took, and Back should undo it the way it undoes any other navigation.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(REVIEW_DOC_PARAM, documentId);
        return next;
      });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    // Replaced, so closing does not leave an entry that Back reopens — a
    // reviewer who closes a review and presses Back means to leave the queue.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(REVIEW_BATCH_PARAM);
        next.delete(REVIEW_DOC_PARAM);
        next.delete(REVIEW_TENANT_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return { documentIds, currentDocumentId, tenantId, open, navigate, close };
}

/** Exported for the tests that pin the cap, so the two cannot drift. */
export const REVIEW_BATCH_LIMIT = MAX_BATCH;
